import { Box, Switch, Typography, Divider } from '@material-ui/core';
import React from 'react';
import { useCampfireLazyQuery } from '../../../../../global/network/useCampfireLazyQuery';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { GET_ALLOW_AD_HOC_REPORTING, GET_ALLOW_LIMITED_AD_HOC_REPORTING } from './ad-hoc-reporting.gql';
import { AdHocRuleManagement } from './AdHocRuleManagement';

// Type definitions for GraphQL responses (until generated types are available)
interface ConfigItemValue {
  flagValue?: boolean;
}

interface ConfigItem {
  configItemId?: string;
  key?: string;
  description?: string;
  defaultValue?: boolean;
  configItemValue?: ConfigItemValue;
}

interface AdHocReportingQueryResponse {
  vm: {
    configItem?: ConfigItem;
  };
}

interface AdHocReportingConfig {
  allowAdHocReporting?: boolean;
  allowLimitedAdHocReporting?: boolean;
}

export function AdHocReporting() {
  const [config, setConfig] = React.useState<AdHocReportingConfig>({});
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireFetch = useCampfireFetch({ defer: true });

  const [queryAllowAdHocReporting] = useCampfireLazyQuery<AdHocReportingQueryResponse, {}>(GET_ALLOW_AD_HOC_REPORTING);
  const [queryAllowLimitedAdHocReporting] = useCampfireLazyQuery<AdHocReportingQueryResponse, {}>(
    GET_ALLOW_LIMITED_AD_HOC_REPORTING
  );

  React.useEffect(() => {
    // Load all config values
    const loadConfigs = async () => {
      try {
        const [adHocResult, allowLimitedResult] = await Promise.all([
          queryAllowAdHocReporting({}),
          queryAllowLimitedAdHocReporting({}),
        ]);

        setConfig({
          allowAdHocReporting:
            adHocResult.data?.vm.configItem?.defaultValue ||
            adHocResult.data?.vm.configItem?.configItemValue?.flagValue ||
            false,
          allowLimitedAdHocReporting:
            allowLimitedResult.data?.vm.configItem?.defaultValue ||
            allowLimitedResult.data?.vm.configItem?.configItemValue?.flagValue ||
            false,
        });
      } catch (error) {
        // Gracefully handle missing backend config items
        console.warn('Ad-hoc reporting config items not found in backend, using defaults');
        setConfig({
          allowAdHocReporting: false,
          allowLimitedAdHocReporting: false,
        });
      }
    };

    loadConfigs();
  }, [queryAllowAdHocReporting, queryAllowLimitedAdHocReporting]);

  const updateConfig = async (key: string, value: boolean) => {
    try {
      await campfireFetch.run({
        url: `${apiUrl}/vm/admin-console/operations/${key}`,
        method: 'post',
        data: { value },
      });

      setSnackbar({
        open: true,
        message: 'Successfully updated',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error updating setting',
        variant: 'error',
      });
    }
  };

  const handleAllowAdHocReportingChange = () => {
    const newValue = !config.allowAdHocReporting;
    setConfig((prev) => ({
      ...prev,
      allowAdHocReporting: newValue,
      // If turning off main toggle, turn off limited toggle as well
      ...(newValue ? {} : { allowLimitedAdHocReporting: false }),
    }));
    updateConfig('allow-ad-hoc-reporting', newValue);
  };

  const handleAllowLimitedChange = () => {
    const newValue = !config.allowLimitedAdHocReporting;
    setConfig((prev) => ({
      ...prev,
      allowLimitedAdHocReporting: newValue,
    }));
    updateConfig('allow-limited-ad-hoc-reporting', newValue);
  };

  // Derived state: allowAll is the inverse of allowLimited
  const allowAll = config.allowAdHocReporting && !config.allowLimitedAdHocReporting;
  const allowLimited = config.allowAdHocReporting && config.allowLimitedAdHocReporting;

  return (
    <Box>
      {/* Main toggle */}
      <Box mt={2}>
        <Box display='flex' alignItems={'center'}>
          <Switch
            checked={Boolean(config.allowAdHocReporting)}
            onChange={handleAllowAdHocReportingChange}
            color='primary'
            inputProps={{ 'aria-label': 'allow ad-hoc reporting' }}
          />
          <Box ml={1}>
            <Typography
              style={{
                fontWeight: 500,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Allow Ad-Hoc Reporting
            </Typography>
            <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
              {'Enable volunteers to create and submit ad-hoc activity reports outside of scheduled sessions'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Conditional sub-toggles with indentation */}
      {config.allowAdHocReporting && (
        <Box ml={6} mt={2} style={{ borderLeft: '2px solid #e0e0e0', paddingLeft: '16px' }}>
          {/* Allow All (read-only display showing inverse of Limited) */}
          <Box mt={2}>
            <Box display='flex' alignItems={'center'}>
              <Switch
                checked={allowAll}
                onChange={handleAllowLimitedChange}
                color='primary'
                inputProps={{ 'aria-label': 'allow all ad-hoc reporting' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Allow All
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  {'All programs and activities are available for ad-hoc reporting by all volunteers'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Allow Limited toggle */}
          <Box mt={2}>
            <Box display='flex' alignItems={'center'}>
              <Switch
                checked={allowLimited}
                onChange={handleAllowLimitedChange}
                color='primary'
                inputProps={{ 'aria-label': 'allow limited ad-hoc reporting' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Allow Limited
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  {
                    'Only programs and activities specifically marked as available for ad-hoc reporting can be used, with role-based access controls similar to visibility settings.'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Show rule management when limited mode is enabled */}
      {allowLimited && (
        <>
          <Box mt={3}>
            <Divider />
          </Box>
          <AdHocRuleManagement />
        </>
      )}
    </Box>
  );
}
